<script>
  import AtomRadioButton from "@/components/atoms/AtomRadioButton.vue";
  import AtomButton from "@/components/atoms/AtomButton.vue";
  import MoleculeReasonSelector from "@/components/molecules/MoleculeReasonSelector.vue";
  import MoleculeTagSelector from "@/components/molecules/MoleculeTagSelector.vue";
  import MoleculeDatePicker from "@/components/molecules/MoleculeDatePicker.vue";
  import MoleculeFileUpload from "@/components/molecules/MoleculeFileUpload.vue";
  import MoleculeCommentSection from "@/components/molecules/MoleculeCommentSection.vue";
  import UndoToast from "@/components/organisms/UndoToast.vue";
  import userStatusHistoryService from "@/services/userStatusHistoryService";
  import userService from "@/services/userService";

  export default {
    name: "StateUserModal",
    components: {
      AtomRadioButton,
      AtomButton,
      MoleculeReasonSelector,
      MoleculeTagSelector,
      MoleculeDatePicker,
      MoleculeFileUpload,
      MoleculeCommentSection,
      UndoToast,
    },
    props: {
      isVisible: {
        type: Boolean,
        default: false,
      },
      user: {
        type: Object,
        default: () => ({}),
      },
      reset: {
        type: Number,
        default: 1,
      },
      mode: {
        type: String,
        default: "inactivacion",
        validator: (v) => ["inactivacion", "licencia", "changeReason", "changeLicenseReason", "cancelLicense"].includes(v),
      },
    },
    data() {
      return {
        selectedState: ["licencia", "changeLicenseReason"].includes(this.mode) ? "licencia" : "inactivacion",
        selectedReason: "",
        comment: "",
        selectedFile: null,
        showDatePicker: false,
        dateFrom: null,
        dateTo: null,
        tags: [],
        tagsSelected: [],
        textTag: "otro",
        showInfoPanel: false,
        options: {
          month: "short",
          day: "numeric",
          timeZone: "America/Bogota",
        },
      };
    },
    computed: {
      optionsDisabled() {
        const options = [
          { key: "endContract", name: this.user?.role === "model" ? "Finalización alianza" : "Termina contrato" },
          { key: "waiver", name: "Renuncia" },
        ];
        if (this.user?.role === "model") {
          options.unshift({ key: "deserter", name: "Deserta" });
          options.push({ key: "admin", name: "Administrativo" });
        }
        return options;
      },

      optionsLicence() {
        return [
          { key: "vacaciones", name: "Vacaciones" },
          { key: "incapacidad", name: "Incapacidad" },
          { key: "licencia", name: "Otro" },
        ];
      },

      optionsChangeReason() {
        return [
          { key: "endContract", name: this.user?.role === "model" ? "Finalización alianza" : "Termina contrato" },
          { key: "waiver", name: "Renuncia" },
        ];
      },

      availableReasons() {
        if (["changeReason", "cancelLicense"].includes(this.mode)) return this.optionsChangeReason;
        return this.selectedState === "licencia" ? this.optionsLicence : this.optionsDisabled;
      },

      shouldShowDatePicker() {
        return this.selectedState === "licencia" && ["vacaciones", "incapacidad", "licencia"].includes(this.selectedReason);
      },

      shouldShowFileUpload() {
        return ["endContract", "waiver", "incapacidad"].includes(this.selectedReason);
      },

      shouldShowComment() {
        return ["endContract", "waiver", "deserter", "licencia", "vacaciones", "incapacidad", "admin"].includes(this.selectedReason);
      },

      shouldShowTags() {
        return ["endContract", "waiver", "deserter", "admin", "incapacidad"].includes(this.selectedReason);
      },
      modalTitle() {
        const titles = {
          licencia: "Creación de licencia",
          inactivacion: "Inactivación de usuario",
          changeReason: "Cambio motivo de inactivación",
          changeLicenseReason: "Cambio motivo de licencia",
          cancelLicense: "Cancelación de licencia",
        };
        return titles[this.mode];
      },
      buttonText() {
        if (this.mode === "licencia") return "Otorgar licencia";
        if (this.mode === "changeLicenseReason") return "Actualizar";
        if (this.mode === "cancelLicense") return "Cancelar licencia";
        return "Inactivar";
      },
    },
    watch: {
      reset() {
        this.resetModalState();
      },
      selectedState() {
        this.selectedReason = "";
        this.showDatePicker = false;
        this.dateFrom = null;
        this.dateTo = null;
      },

      async selectedReason(newReason) {
        if (["vacaciones", "incapacidad", "licencia"].includes(newReason)) {
          this.showDatePicker = true;
          if (!this.dateFrom && !this.dateTo) {
            this.$nextTick(() => {
              setTimeout(() => {
                if (this.$refs.datePicker && this.$refs.datePicker.openPicker) {
                  this.$refs.datePicker.openPicker();
                }
              }, 100);
            });
          }
        } else {
          this.showDatePicker = false;
          this.dateFrom = null;
          this.dateTo = null;
        }

        if (["endContract", "waiver", "deserter", "admin", "incapacidad"].includes(newReason)) {
          this.tags = await this.getTags(newReason);
          this.tagsSelected.length = 0;
        } else {
          this.tags = [];
          this.tagsSelected = [];
        }
      },
    },
    methods: {
      onStateChange() {
        this.selectedReason = "";
        this.dateFrom = null;
        this.dateTo = null;
      },
      onReasonClick(reasonKey) {
        this.selectedReason = reasonKey;

        if (!["vacaciones", "incapacidad", "licencia"].includes(reasonKey)) {
          this.dateFrom = null;
          this.dateTo = null;
        }

        if (["vacaciones", "incapacidad", "licencia"].includes(reasonKey) && !this.dateFrom && !this.dateTo) {
          this.$nextTick(() => {
            setTimeout(() => {
              if (this.$refs.datePicker && this.$refs.datePicker.openPicker) {
                this.$refs.datePicker.openPicker();
              }
            }, 200);
          });
        }
      },
      onFileSelect(file) {
        this.selectedFile = file;
      },

      handleDateChange({ dateFrom, dateTo }) {
        if (!dateFrom || !dateTo) return;
        if (!(dateFrom instanceof Date) || !(dateTo instanceof Date)) return;

        if (isNaN(dateFrom.getTime()) || isNaN(dateTo.getTime())) return;

        this.dateFrom = dateFrom;
        this.dateTo = dateTo;
      },

      validateForm() {
        if (!this.selectedReason) return false;

        const requirements = {
          endContract: ["comment", "tags"],
          waiver: ["comment", "tags"],
          deserter: ["comment", "tags"],
          licencia: ["comment", "dateFrom", "dateTo"],
          vacaciones: ["comment", "dateFrom", "dateTo"],
          incapacidad: ["comment", "dateFrom", "dateTo", "tags"],
          admin: ["comment", "tags"],
        };

        const requiredFields = requirements[this.selectedReason] || [];

        return requiredFields.every((field) => {
          if (field === "comment") return this.comment?.trim();
          if (field === "dateFrom") return this.dateFrom;
          if (field === "dateTo") return this.dateTo;
          if (field === "tags") return this.tagsSelected.length > 0;
          return true;
        });
      },

      closeModal() {
        this.resetModalState();
        this.$emit("closeModal");
      },

      resetModalState() {
        this.selectedState = ["licencia", "changeLicenseReason"].includes(this.mode) ? "licencia" : "inactivacion";
        this.selectedReason = "";
        this.comment = "";
        this.selectedFile = null;
        this.showDatePicker = false;
        this.dateFrom = null;
        this.dateTo = null;
        this.tags = [];
        this.tagsSelected = [];
        this.textTag = "otro";
      },

      async saveChanges() {
        if (!this.validateForm()) {
          console.warn("Please fill all required fields");
          return;
        }

        try {
          if (this.mode === "changeReason" || this.mode === "cancelLicense") {
            const historyData = {
              reason: this.selectedReason,
              description: this.comment,
              tags: this.tagsSelected.length ? [...new Set(this.tagsSelected)] : undefined,
              suportFile: this.selectedFile,
              eventType: "disable",
              startDate: null,
              endDate: null,
            };
            await userStatusHistoryService.updateUserStatusHistory(this.user.userStatusHistory._id, historyData);
            const freshUser = await userService.getUserData(this.user._id);
            this.$emit("refreshUser", freshUser);
            return;
          }

          if (this.mode === "changeLicenseReason") {
            const historyData = {
              reason: this.selectedReason,
              description: this.comment,
              tags: this.tagsSelected.length ? [...new Set(this.tagsSelected)] : undefined,
              suportFile: this.selectedFile,
              eventType: "license",
              startDate: this.dateFrom,
              endDate: this.dateTo,
            };
            await userStatusHistoryService.updateUserStatusHistory(this.user.userStatusHistory._id, historyData);
            const freshUser = await userService.getUserData(this.user._id);
            this.$emit("refreshUser", freshUser);
            return;
          }

          if (this.mode === "licencia") {
            const historyData = {
              reason: this.selectedReason,
              description: this.comment,
              userId: this.user._id,
              tags: this.tagsSelected.length ? [...new Set(this.tagsSelected)] : undefined,
              startDate: this.dateFrom,
              endDate: this.dateTo,
              eventType: "license",
              suportFile: this.selectedFile,
            };

            await this.createReasonDisabled(historyData);

            if (new Date(this.dateFrom).toISOString().split("T")[0] <= new Date().toISOString().split("T")[0]) {
              const userData = {
                _id: this.user._id,
                reason: this.selectedReason,
                isEnable: false,
              };
              this.$emit("save", userData);
              return;
            }

            const freshUser = await userService.getUserData(this.user._id);
            this.$emit("refreshUser", freshUser);
          } else {
            const historyData = {
              reason: this.selectedReason,
              description: this.comment,
              userId: this.user._id,
              tags: this.tagsSelected.length ? [...new Set(this.tagsSelected)] : undefined,
              startDate: this.dateFrom,
              endDate: this.dateTo,
              eventType: "disable",
              suportFile: this.selectedFile,
            };

            await this.createReasonDisabled(historyData);

            const userData = {
              _id: this.user._id,
              reason: this.selectedReason,
              isEnable: false,
            };

            this.$emit("save", userData);
          }
        } catch (error) {
          console.error("Error saving user state:", error);
        }
      },

      async createReasonDisabled(data) {
        try {
          const dataToSend = {
            ...data,
            description: data.description,
            userId: this.user._id,
            tags: data.tags,
            startDate: data.startDate,
            endDate: data.endDate,
            eventType: data.eventType,
            eventDate: new Date(),
          };

          await userStatusHistoryService.sendUserStatusHistory(dataToSend);
        } catch (error) {
          console.error("Error creating reason disabled:", error);
          throw error;
        }
      },
      saveUserState() {
        const data = {
          state: this.selectedState,
          reason: this.selectedReason,
          comment: this.comment,
          file: this.selectedFile,
          dateFrom: this.dateFrom,
          dateTo: this.dateTo,
          tagsSelected: this.tagsSelected.length ? [...new Set(this.tagsSelected)] : undefined,
        };
        this.$emit("save", data);
      },
      cancelChanges() {
        this.closeModal();
      },
      setTagSelected(valTagId) {
        const tagIdx = this.tagsSelected.findIndex((tagId) => tagId === valTagId);

        if (tagIdx !== -1) {
          this.tagsSelected.splice(tagIdx, 1);
        } else {
          this.tagsSelected = [valTagId];
        }
      },

      async createTag(name) {
        this.textTag = "otro";

        if (!name || name === "otro") {
          return;
        }

        const existTag = this.tags.find((tag) => tag.name === name);

        if (existTag) {
          this.tagsSelected = [existTag._id];
          return;
        }

        try {
          const { data } = await this.$axios.post(`/tags`, { name, categories: [this.selectedReason] });
          const { success, tagCreated } = data;

          if (success) {
            this.tags.push(tagCreated);
            this.tagsSelected = [tagCreated._id];
          }
        } catch (error) {
          console.error("Error creating tag:", error);
        }
      },

      async getTags(categories) {
        try {
          const { data } = await this.$axios.get(`/tags?categories=${categories}`);
          const { tags = [] } = data;
          return tags;
        } catch (error) {
          console.error("Error getting tags:", error);
          return [];
        }
      },

      async undoCreateTag(deletedTag, getToastId) {
        try {
          const payload = { isEnable: true };
          const { data } = await this.$axios.put(`/tags/${deletedTag._id}`, payload);
          const { success, tagDeleted } = data;
          if (success && tagDeleted) {
            this.tags.push(tagDeleted);
            this.tagsSelected = [tagDeleted._id];
          }

          const toastId = typeof getToastId === "function" ? getToastId() : null;
          if (toastId && this.$toast && typeof this.$toast.dismiss === "function") {
            this.$toast.dismiss(toastId);
          }
        } catch (e) {
          console.error("Error undo creating tag", e);
          if (this.$toast && typeof this.$toast.error === "function") {
            this.$toast.error("No se pudo deshacer la acción");
          }
        }
      },

      dismissToast(getToastId) {
        try {
          const toastId = typeof getToastId === "function" ? getToastId() : null;
          if (toastId && this.$toast && typeof this.$toast.dismiss === "function") {
            this.$toast.dismiss(toastId);
          }
        } catch (e) {
          console.log("Error closing toast", e);
        }
      },

      async removeTag(tagId) {
        const tagIdx = this.tags.findIndex((tag) => tag._id === tagId);
        if (tagIdx === -1) return;

        const deletedTag = this.tags[tagIdx];

        try {
          await this.$axios.put(`/tags/${tagId}`, { isEnable: false });
          this.tags.splice(tagIdx, 1);

          if (this.$toast && typeof this.$toast.success === "function") {
            let toastId = null;

            toastId = this.$toast.success(
              {
                component: UndoToast,
                props: {
                  message: `"${deletedTag.name}"`,
                  actionText: "Deshacer",
                  onUndo: () => this.undoCreateTag(deletedTag, () => toastId),
                  closeToast: () => this.dismissToast(() => toastId),
                },
              },
              {
                position: "bottom-center",
                timeout: 5000,
                closeOnClick: true,
                pauseOnFocusLoss: true,
                pauseOnHover: true,
                draggable: true,
                draggablePercent: 0.6,
                closeButton: false,
                showCloseButtonOnHover: false,
                hideProgressBar: true,
                icon: true,
                rtl: false,
              },
            );
          }
        } catch (error) {
          console.error("Error deleting tag:", error);
          if (this.$toast && typeof this.$toast.error === "function") {
            this.$toast.error("Error al eliminar el tag");
          }
        }
      },
    },
  };
</script>

<template>
  <div v-if="isVisible" class="stateUserModal">
    <div class="stateUserModal__overlay" @click="closeModal"></div>
    <div class="stateUserModal__container">
      <div :class="['stateUserModal__header', { 'stateUserModal__header--hidden': showInfoPanel }]">
        <div class="stateUserModal__headerActions">
          <p class="stateUserModal__title">{{ modalTitle }}</p>
          <button v-if="['inactivacion', 'licencia'].includes(mode)" @click="showInfoPanel = true" class="stateUserModal__infoButton">
            <iconic name="informationIcon" />
          </button>
        </div>
        <button @click="closeModal" class="stateUserModal__closeButton">
          <iconic name="close" />
        </button>
      </div>

      <div v-if="showInfoPanel" class="stateUserModal__infoPanel">
        <div class="stateUserModal__infoPanelHeader">
          <p class="stateUserModal__infoPanelTitle">¿Cómo funciona el flujo?</p>
          <button @click="showInfoPanel = false" class="stateUserModal__infoPanelClose">Cerrar</button>
        </div>

        <div class="stateUserModal__infoPanelContent">
          <div class="stateUserModal__infoPanelSection">
            <p class="stateUserModal__infoPanelLabel">Tipos de inactivación</p>
            <div class="stateUserModal__infoPanelTypes">
              <div class="stateUserModal__infoPanelTypeCard" :class="{ 'stateUserModal__infoPanelTypeCard--active': mode === 'inactivacion' }">
                <p class="stateUserModal__infoPanelTypeTitle">Inactivación</p>
                <p class="stateUserModal__infoPanelTypeDesc">La modelo ya no trabaja con la empresa. Se elige un motivo específico del cese.</p>
              </div>
              <div class="stateUserModal__infoPanelTypeCard" :class="{ 'stateUserModal__infoPanelTypeCard--active': mode === 'licencia' }">
                <p class="stateUserModal__infoPanelTypeTitle">Licencia</p>
                <p class="stateUserModal__infoPanelTypeDesc">Ausencia temporal. El estado puede cambiar si la modelo avisa que no regresa.</p>
              </div>
            </div>
          </div>

          <div class="stateUserModal__infoPanelSection">
            <p class="stateUserModal__infoPanelLabel">Motivos de inactivación</p>
            <div class="stateUserModal__infoPanelReasons">
              <div class="stateUserModal__infoPanelReason">
                <span class="stateUserModal__infoPanelDot stateUserModal__infoPanelDot--red"></span>
                <div>
                  <p class="stateUserModal__infoPanelReasonTitle">Deserta</p>
                  <p class="stateUserModal__infoPanelReasonDesc">Desaparece sin comunicación</p>
                </div>
              </div>
              <div class="stateUserModal__infoPanelReason">
                <span class="stateUserModal__infoPanelDot stateUserModal__infoPanelDot--red"></span>
                <div>
                  <p class="stateUserModal__infoPanelReasonTitle">Finalización de alianza</p>
                  <p class="stateUserModal__infoPanelReasonDesc">Acuerdo terminado formalmente</p>
                </div>
              </div>
              <div class="stateUserModal__infoPanelReason">
                <span class="stateUserModal__infoPanelDot stateUserModal__infoPanelDot--red"></span>
                <div>
                  <p class="stateUserModal__infoPanelReasonTitle">Renuncia</p>
                  <p class="stateUserModal__infoPanelReasonDesc">La modelo decide retirarse</p>
                </div>
              </div>
              <div class="stateUserModal__infoPanelReason">
                <span class="stateUserModal__infoPanelDot stateUserModal__infoPanelDot--orange"></span>
                <div>
                  <p class="stateUserModal__infoPanelReasonTitle">Administrativo</p>
                  <p class="stateUserModal__infoPanelReasonDesc">Requiere reunión previa — selecciona un tag</p>
                  <div class="stateUserModal__infoPanelTags">
                    <span class="stateUserModal__infoPanelTag">Gerencia</span>
                    <span class="stateUserModal__infoPanelTag">Calidad</span>
                    <span class="stateUserModal__infoPanelTag">Administración</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="stateUserModal__infoPanelSection">
              <p class="stateUserModal__infoPanelLabel">Cambios de estado posibles</p>
              <div class="stateUserModal__infoPanelReasons">
                <div class="stateUserModal__infoPanelReason">
                  <span class="stateUserModal__infoPanelDot stateUserModal__infoPanelDot--red"></span>
                  <div>
                    <p class="stateUserModal__infoPanelReasonTitle">Desde Administrativo</p>
                    <p class="stateUserModal__infoPanelReasonDesc">licencia, renuncia, finalización de alianza o activacion</p>
                  </div>
                </div>
                <div class="stateUserModal__infoPanelReason">
                  <span class="stateUserModal__infoPanelDot stateUserModal__infoPanelDot--grey"></span>
                  <div>
                    <p class="stateUserModal__infoPanelReasonTitle">Desde Licencia</p>
                    <p class="stateUserModal__infoPanelReasonDesc">Renuncia, activacion anticipada</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="stateUserModal__body">
        <div v-if="mode === 'changeReason' || mode === 'changeLicenseReason'" class="stateUserModal__warning">
          <iconic name="danger_icon" class="stateUserModal__warningIcon" />
          <span v-html="'<strong>El usuario permanece inactivo.</strong> Solo se actualiza el motivo registrado.'"> </span>
        </div>

        <div class="stateUserModal__reasonSection">
          <MoleculeReasonSelector title="Motivo" :options="availableReasons" :selected-reason="selectedReason" radio-group-name="reasonGroup" @reason-selected="onReasonClick" />
        </div>

        <div v-if="shouldShowTags" class="stateUserModal__tagsSection">
          <p class="stateUserModal__tagsText">Por favor, selecciona un tag que defina el motivo.</p>
          <MoleculeTagSelector
            :tags="tags"
            :selected-tags="tagsSelected"
            :canDeleteAndCreateTags="!['admin', 'incapacidad'].includes(selectedReason)"
            new-tag-placeholder="otro"
            @tag-selected="setTagSelected"
            @tag-created="createTag"
            @tag-removed="removeTag" />
        </div>

        <div v-if="shouldShowDatePicker" :class="['stateUserModal__dateSection', { 'stateUserModal__dateSection--expanded': !dateFrom }]">
          <MoleculeDatePicker ref="datePicker" title="Fecha" placeholder="Seleccionar fecha" :date-from="dateFrom" :date-to="dateTo" @date-changed="handleDateChange" />
        </div>

        <div v-if="shouldShowFileUpload" class="stateUserModal__supportSection">
          <MoleculeFileUpload title="Soportes" placeholder="Selecciona un archivo" :selected-file="selectedFile" @file-selected="onFileSelect" />
        </div>

        <div class="stateUserModal__commentSection">
          <MoleculeCommentSection
            :label="mode === 'changeReason' ? 'Razón del cambio' : selectedState === 'licencia' ? 'Descripción' : 'Comentario'"
            :placeholder="selectedState === 'licencia' ? 'Descripción de licencia' : 'Descripción inactivación'"
            :model-value="comment"
            :required="true"
            :max-rows="4"
            @input="comment = $event" />
        </div>

        <div class="stateUserModal__actions">
          <AtomButton variant="secondary" @click="cancelChanges"> Cancelar </AtomButton>
          <AtomButton variant="primary" :disabled="!validateForm()" @click="saveChanges">{{ buttonText }}</AtomButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
  .stateUserModal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;

    &__overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.5);
    }

    &__container {
      position: relative;
      background: white;
      border-radius: 12px;
      width: 90%;
      max-width: 550px;
      max-height: 90vh;
      overflow-y: auto;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px;

      &--hidden {
        visibility: hidden;
      }
    }

    &__title {
      margin: 0;
      font-size: 24px;
      font-weight: 600;
      font-family: $newFont_bold;
      color: $primary-color;
    }
    &__headerActions {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 6px;
    }
    &__closeButton {
      @include Flex();
      background: none;
      border: none;
      cursor: pointer;
      color: #666;

      &:hover {
        color: #333;
      }
    }
    &__infoButton {
      background: none;
      border: none;
      padding: 6px;
      border-radius: 6px;
      font-size: 18px;
      color: #3d3d3d;
      display: flex;
      align-items: center;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background: #f5f5f5;
      }
    }
    &__infoPanel {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: #fff;
      border-radius: 12px;
      z-index: 10;
      overflow-y: auto;
      display: flex;
      flex-direction: column;

      &::-webkit-scrollbar {
        width: 4px;
      }
      &::-webkit-scrollbar-thumb {
        border-radius: 50px;
        background: #a5a5a5;
      }
    }
    &__infoPanelHeader {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px 20px 0;
    }
    &__infoPanelTitle {
      font-family: $newFont_bold;
      font-size: 20px;
      font-weight: 700;
      color: $primary-color;
      margin: 0;
    }
    &__infoPanelClose {
      background: none;
      border: 1px solid #d9d9d9;
      border-radius: 6px;
      padding: 6px 14px;
      font-family: $newFont_regular;
      font-size: 14px;
      color: #3d3d3d;
      cursor: pointer;

      &:hover {
        background: #f5f5f5;
      }
    }
    &__infoPanelContent {
      display: flex;
      flex-direction: column;
      gap: 20px;
      padding: 20px;
    }
    &__infoPanelSection {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    &__infoPanelLabel {
      font-family: $newFont_medium;
      font-size: 14px;
      font-weight: 400;
      color: #3d3d3d;
      margin: 0;
      border-bottom: 1px solid rgba(189, 9, 9, 0.2);
    }
    &__infoPanelTypes {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
    }
    &__infoPanelTypeCard {
      padding: 12px 14px;
      border: 1px solid #f0f0f0;
      background: #fafafa;
      border-radius: 8px;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    &__infoPanelTypeTitle {
      font-family: $newFont_medium;
      font-size: 14px;
      font-weight: 600;
      color: #3d3d3d;
      margin: 0;
    }
    &__infoPanelTypeDesc {
      font-family: $newFont_regular;
      font-size: 12px;
      color: #595959;
      margin: 0;
      line-height: 1.4;
    }
    &__infoPanelReasons {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    &__infoPanelReason {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 10px 12px;
      border: 1px solid #f0f0f0;
      background: #fafafa;
      border-radius: 8px;
    }
    &__infoPanelDot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      flex-shrink: 0;
      margin-top: 4px;
      &--red {
        background: $primary-color;
      }
      &--orange {
        background: #c77700;
      }
      &--grey {
        background: #9d9d9d;
      }
    }
    &__infoPanelReasonTitle {
      font-family: $newFont_medium;
      font-size: 14px;
      font-weight: 500;
      color: #3d3d3d;
      margin: 0 0 2px;
    }
    &__infoPanelReasonDesc {
      font-family: $newFont_regular;
      font-size: 12px;
      color: #9d9d9d;
      margin: 0;
    }
    &__infoPanelTags {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-top: 6px;
    }
    &__infoPanelTag {
      padding: 3px 10px;
      border-radius: 20px;
      font-family: $newFont_regular;
      font-size: 12px;
      color: $primary-color;
      border-radius: 6px;
      border: 1px solid rgba(189, 9, 9, 0.17);
      background: #fff9f9;
    }

    &__body {
      padding: 0 20px 20px;
    }

    &__stateSection {
      margin-bottom: 20px;
    }

    &__radioGroup {
      display: flex;
      gap: 20px;
    }

    &__warning {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 14px;
      margin-bottom: 20px;
      background: rgba(47, 157, 255, 0.07);
      border-radius: 8px;
      font-size: 14px;
      color: #3d3d3d;
      border: 1px solid rgba(47, 157, 255, 0.2);
      font-family: $newFont_regular;
    }

    &__warningIcon {
      flex-shrink: 0;
      font-size: 16px;
      color: #3d3d3d;
    }

    &__tagsSection,
    &__reasonSection,
    &__supportSection,
    &__dateSection,
    &__commentSection {
      margin-bottom: 20px;
    }

    &__tagsText {
      color: #595959;
      font-size: 14px;
      font-family: $newFont_regular;
      margin-bottom: 10px;
    }

    &__dateSection {
      &--expanded {
        min-height: 335px;
      }
    }

    &__actions {
      display: flex;
      justify-content: flex-end;
      gap: 20px;
      padding-top: 20px;
    }
  }
  .Vue-Toastification__toast-component-body {
    display: flex !important;
  }
  .Vue-Toastification {
    &__toast {
      background-color: #ffff;
      max-height: 59px;
      min-height: 6px;
      padding: 16px 24px;
      &-body {
        color: #000;
      }
      &--success {
        .Vue-Toastification__icon {
          color: #4caf50;
        }
        .Vue-Toastification__close-button {
          color: #000;
        }
      }
      &--error {
        .Vue-Toastification__icon {
          color: $primary;
        }
        .Vue-Toastification__close-button {
          color: #000;
        }
      }
      &--info {
        .Vue-Toastification__icon {
          color: #efefef;
        }
        .Vue-Toastification__info-button {
          color: #000;
        }
      }
    }
  }
</style>
